import type { ReactNode } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ErrorSurfaceProps {
  /**
   * Renders the surface as the page's `<main>` landmark.
   *
   * The design brief (T104 § Sibling already designed) declares two states for
   * the shell: *with-global-header* and *without-global-header*. They map onto
   * where the surface is mounted, and the landmark must follow:
   *
   * - `false` (default) — mounted inside a route, so `root.tsx` has already
   *   rendered `<main id="main-content">` around the `<Outlet />`. The surface
   *   is a labelled `<section>`; a nested `<main>` here would be a second,
   *   invalid landmark.
   * - `true` — mounted in the root `ErrorBoundary`, which renders its own
   *   `<html>`/`<body>` with no app chrome. There is no other landmark, so the
   *   surface owns `<main>`.
   */
  standalone?: boolean;
  /** Contents of the `.af-empty-mark` atom — a numeric glyph or an icon. */
  mark: ReactNode;
  /** Paints the mark in the danger tone. Reserved for 500 — offline is a state, not an error. */
  markWarn?: boolean;
  /** Mono eyebrow above the title. Either a plain string or the `.af-eyebrow-row` variant. */
  eyebrow: ReactNode;
  title: string;
  sub: ReactNode;
  /** Primary CTA — one per surface. */
  primary: ReactNode;
  /** Secondary CTA. Wrapped in `.es-link-block` so `.af-link` clears the 44px hit target. */
  secondary?: ReactNode;
  /** Mono meta rows in the page foot. */
  footer?: ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Shared shell for the three terminal error surfaces (404 · 500 · offline).
 *
 * Owns the `.es-*` page grid and nothing else — mark, eyebrow, title, sub,
 * primary and secondary are all `.af-*` atoms from the auth tier
 * (`app/styles/app.css`, ported by T97 / reconciled by T223). Zero new atoms,
 * per the T104 design contract.
 *
 * HTTP status codes, incident-id generation and the observability hook are
 * explicitly NOT this component's business — that plumbing is T38.
 */
export function ErrorSurface({
  standalone = false,
  mark,
  markWarn = false,
  eyebrow,
  title,
  sub,
  primary,
  secondary,
  footer,
}: ErrorSurfaceProps) {
  const Shell = standalone ? "main" : "section";

  return (
    // `is-standalone` carries the height regime, not just a style flag: with the app
    // chrome around it the surface must fill what <main> leaves, and only without the
    // chrome may it claim the viewport. See the `.es-page` comment in app.css.
    <Shell
      className={standalone ? "es-page is-standalone" : "es-page"}
      aria-labelledby="es-title"
    >
      <section className="es-main">
        <div
          className={markWarn ? "af-empty-mark is-warn" : "af-empty-mark"}
          aria-hidden="true"
        >
          {mark}
        </div>

        <div className="af-form-head">
          {eyebrow}
          <h1 className="af-form-title" id="es-title">
            {title}
          </h1>
          <p className="af-form-sub">{sub}</p>
        </div>

        {primary}

        {secondary ? <span className="es-link-block">{secondary}</span> : null}
      </section>

      {footer ? <footer className="es-bottom">{footer}</footer> : null}
    </Shell>
  );
}
